import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Download, LogOut, Activity, Smartphone, MessageSquare, MapPinned, User, ChevronDown, Settings, HelpCircle, MessageCircle, CreditCard, Users as UsersIcon, Package } from 'lucide-react';
import TraqPackMap from './components/TraqPackMap';
import TimeFilter from './components/TimeFilter';
import StatsGrid from './components/StatsGrid';
import AlertsPanel from './components/AlertsPanel';
import DataTable from './components/DataTable';
import LoginWithSignup from './components/LoginWithSignup';
import EditDeviceModal from './components/EditDeviceModal';
import GeofenceManager from './components/GeofenceManager';
import GeofenceModal from './components/GeofenceModal';
import GeofenceNotifications from './components/GeofenceNotifications';
import DeviceSelector from './components/DeviceSelector';
import DevicesPageUserFiltered from './components/DevicesPageUserFiltered';
import UsersPage from './components/UsersPage';
import ShipmentsPage from './components/ShipmentsPage';
import DeviceStats from './components/DeviceStats';
import { AIQueryBar } from './components/AIQueryBar';
import AIChatPanel from './components/AIChatPanel';
import { supabase, GpsData, Alert, Device, Geofence } from './lib/supabase';
import { useSupabaseAuth } from './contexts/SupabaseAuthContext';
import './App.css';

function AppMultiTenant() {
  const { isAuthenticated, signOut, user } = useSupabaseAuth();
  const [activeTab, setActiveTab] = useState<'tracking' | 'devices' | 'shipments' | 'users'>('tracking');
  const [timeFilter, setTimeFilter] = useState<string>('1d');
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
  const [gpsData, setGpsData] = useState<GpsData[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [stats, setStats] = useState({
    totalDevices: 0,
    activeDevices: 0,
    totalReadings: 0,
    recentReadings: 0,
  });
  const [loading, setLoading] = useState(true);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [searchTerm] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedDeviceForEdit, setSelectedDeviceForEdit] = useState<Device | null>(null);
  const [geofences, setGeofences] = useState<Geofence[]>([]);
  const [selectedGeofence, setSelectedGeofence] = useState<string | null>(null);
  const [isDrawingGeofence, setIsDrawingGeofence] = useState(false);
  const [geofenceModalOpen, setGeofenceModalOpen] = useState(false);
  const [editingGeofence, setEditingGeofence] = useState<Geofence | null>(null);
  const [temporaryGeofence, setTemporaryGeofence] = useState<{ center: [number, number]; radius: number } | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [chatPanelOpen, setChatPanelOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [userProfile, setUserProfile] = useState<{ full_name: string; email: string; customer_id?: string } | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);

  // Close user menu when clicking outside (with delay to allow button clicks)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      // Don't close if clicking on the menu or its children
      if (userMenuOpen && !target.closest('[data-user-menu]')) {
        setTimeout(() => setUserMenuOpen(false), 100);
      }
    };

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);

  // Check if user is super admin and fetch user profile
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (user?.id) {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('is_super_admin, full_name, email, customer_id')
          .eq('id', user.id)
          .single();
        
        if (data && !error) {
          setIsSuperAdmin(data.is_super_admin || false);
          setUserProfile({
            full_name: data.full_name || '',
            email: data.email || user.email || '',
            customer_id: data.customer_id || null
          });
          setCustomerId(data.customer_id || null);
        } else if (error?.code === 'PGRST116') {
          // Profile doesn't exist, create one
          const newProfile = {
            id: user.id,
            email: user.email || '',
            full_name: user.user_metadata?.full_name || '',
            is_super_admin: false
          };
          
          const { data: createdProfile } = await supabase
            .from('user_profiles')
            .insert(newProfile)
            .select()
            .single();
          
          if (createdProfile) {
            setUserProfile({
              full_name: createdProfile.full_name || '',
              email: createdProfile.email || user.email || '',
              customer_id: createdProfile.customer_id || null
            });
            setCustomerId(createdProfile.customer_id || null);
          }
        }
      }
    };
    
    fetchUserProfile();
  }, [user]);

  const loadGpsData = useCallback(async (deviceId?: string) => {
    if (!user?.id) return;
    
    try {
      setGpsLoading(true);
      
      // Get GPS data with tenant filtering
      // Support timeFilter values like: '1h', '1d', '1w', '1m'
      const num = parseInt(timeFilter.replace(/[^\d]/g, '')) || 1;
      let hours: number;
      if (timeFilter.endsWith('h')) {
        hours = num;
      } else if (timeFilter.endsWith('d')) {
        hours = num * 24;
      } else if (timeFilter.endsWith('w')) {
        hours = num * 24 * 7;
      } else if (timeFilter.endsWith('m')) {
        hours = num * 24 * 30; // approximate month
      } else {
        hours = 24; // default to 1 day
      }
      const since = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
      
      // Get effective customer_id first
      let effectiveCustomerId = customerId;
      if (!effectiveCustomerId) {
        const { data: prof } = await supabase
          .from('user_profiles')
          .select('customer_id')
          .eq('id', user.id)
          .single();
        effectiveCustomerId = prof?.customer_id || null;
        console.log('DEBUG: Got customer_id from user_profiles:', { user_id: user.id, customer_id: effectiveCustomerId });
      } else {
        console.log('DEBUG: Using existing customer_id:', effectiveCustomerId);
      }

      // TEMPORARY: Use debug RPC directly since we know it works
      let { data, error } = effectiveCustomerId 
        ? await supabase.rpc('get_customer_gps_data_debug', {
            p_customer_id: effectiveCustomerId,
            p_time_window: timeFilter,
            p_device_id: deviceId || null
          })
        : { data: null, error: { message: 'No customer_id available' } };

      // Debug log to see raw results count
      console.log('RPC get_customer_gps_data_debug result:', { count: data?.length || 0, error, effectiveCustomerId });
      
      // DETAILED DEBUG - show exactly what we're getting
      if (effectiveCustomerId) {
        console.log('=== DETAILED GPS DEBUG ===');
        console.log('User ID:', user.id);
        console.log('Customer ID:', effectiveCustomerId);
        console.log('Time Filter:', timeFilter);
        console.log('Device ID:', deviceId);
        console.log('RPC Data Count:', data?.length || 0);
        console.log('RPC Error:', error);
        if (data && data.length > 0) {
          console.log('First GPS Record:', data[0]);
        }
        console.log('========================');
        
        // PARALLEL TEST: Direct query to see if data exists at all
        const { data: testData, error: testError } = await supabase
          .from('gps_data')
          .select('device_id, created_at')
          .eq('device_id', '251075040506')
          .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
          .limit(5);
        console.log('DIRECT TEST - 251075040506 last 24h:', { count: testData?.length || 0, error: testError });
        
        // Test with TEST_ prefix too
        const { data: testData2, error: testError2 } = await supabase
          .from('gps_data')
          .select('device_id, created_at')
          .eq('device_id', 'TEST_251075040506')
          .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
          .limit(5);
        console.log('DIRECT TEST - TEST_251075040506 last 24h:', { count: testData2?.length || 0, error: testError2 });
      }

      // Fallback 2: if still empty and have deviceId, try direct flexible query on gps_data
      if ((data?.length || 0) === 0 && deviceId) {
        const numOnly = deviceId.replace(/[^0-9]/g, '');
        const last12 = numOnly.slice(-12);
        const with863 = numOnly.startsWith('863') ? numOnly : `863${last12}`;
        const withTest = `TEST_${last12}`;

        const num = parseInt(timeFilter.replace(/[^\d]/g, '')) || 1;
        let hours: number;
        if (timeFilter.endsWith('h')) hours = num; 
        else if (timeFilter.endsWith('d')) hours = num * 24; 
        else if (timeFilter.endsWith('w')) hours = num * 24 * 7; 
        else if (timeFilter.endsWith('m')) hours = num * 24 * 30; 
        else hours = 24;
        const since = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();

        const { data: fbData, error: fbError } = await supabase
          .from('gps_data')
          .select('*')
          .gte('created_at', since)
          .or(
            `device_id.eq.${last12},device_id.eq.${with863},device_id.eq.${withTest},device_id.like.%${last12}`
          )
          .order('created_at', { ascending: false })
          .limit(1000);

        if (!fbError && (fbData?.length || 0) > 0) {
          data = fbData as any;
        }
      }
      
      console.log('GPS Query Result:', { 
        deviceId, 
        dataCount: data?.length || 0, 
        error,
        timeFilter,
        since,
        isSuperAdmin
      });
      
      if (error) throw error;
      
      // Debug: Log raw_data structure for first few records
      if (data && data.length > 0) {
        console.log('🔍 GPS Data Debug - First record raw_data:', data[0].raw_data);
        console.log('🔍 GPS Data Debug - First record iccid:', data[0].iccid);
        console.log('🔍 GPS Data Debug - First record firmware_version:', data[0].firmware_version);
        
        // Look for device 251075013057 specifically
        const targetDevice = data.find((d: any) => d.device_id === '251075013057');
        if (targetDevice) {
          console.log('🎯 Found target device 251075013057:', {
            device_id: targetDevice.device_id,
            raw_data: targetDevice.raw_data,
            raw_data_iccid: targetDevice.raw_data?.iccid,
            iccid: targetDevice.iccid,
            firmware_version: targetDevice.firmware_version
          });
        }
      }
      
      setGpsData(data || []);
      
      // Update stats
      setStats(prev => ({
        ...prev,
        totalReadings: data?.length || 0,
        recentReadings: data?.filter((d: GpsData) => 
          new Date(d.created_at).getTime() > Date.now() - 60 * 60 * 1000
        ).length || 0,
      }));
      
    } catch (error) {
      console.error('Error loading GPS data:', error);
    } finally {
      setGpsLoading(false);
    }
  }, [user, timeFilter, isSuperAdmin, customerId]);

  const loadData = useCallback(async () => {
    if (!user?.id) {
      setDevices([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Get devices based on user role
      let userDevices;
      if (isSuperAdmin) {
        // Super admin: get all devices
        const { data, error } = await supabase.rpc('get_all_devices_admin');
        if (error) throw error;
        userDevices = data;
      } else {
        // Regular user: get only their devices
        const { data, error } = await supabase.rpc('get_user_devices');
        if (error) throw error;
        userDevices = data;
      }
      
      // Transform to Device interface
      const transformedDevices: Device[] = (userDevices || []).map((d: any) => ({
        id: d.device_id,
        device_id: d.device_id,
        device_nickname: d.device_nickname || `Device ${d.device_id}`,
        device_type: d.device_type || 'GPS Tracker',
        is_active: d.is_active ?? false,
        last_seen: d.last_seen,
        last_latitude: d.last_latitude,
        last_longitude: d.last_longitude,
        last_temperature: d.last_temperature,
        last_water_confidence: d.last_water_confidence,
        last_speed: d.last_speed,
        last_heading: d.last_heading,
        battery_percentage: d.battery_percentage,
        cell_signal_strength: d.cell_signal_strength,
        claimed_at: d.claimed_at,
        
        // Device identification fields (NEW)
        firmware_version: d.firmware_version,
        iccid: d.iccid,
        last_firmware_update: d.last_firmware_update
      }));

      // Deduplicate by device_id (prefer most recent last_seen)
      const byId = new Map<string, Device>();
      transformedDevices.forEach((dev) => {
        const existing = byId.get(dev.device_id);
        if (!existing) {
          byId.set(dev.device_id, dev);
          return;
        }
        const tNew = dev.last_seen ? Date.parse(dev.last_seen) : 0;
        const tOld = existing.last_seen ? Date.parse(existing.last_seen) : 0;
        if (tNew >= tOld) {
          byId.set(dev.device_id, dev);
        }
      });
      const dedupedDevices = Array.from(byId.values());

      setDevices(dedupedDevices);
      
      console.log('Loaded devices:', {
        count: transformedDevices.length,
        devices: transformedDevices,
        isSuperAdmin,
        selectedDevice
      });
      
      // Calculate stats
      const activeCount = dedupedDevices.filter(d => d.is_active).length;
      setStats(prev => ({
        ...prev,
        totalDevices: dedupedDevices.length,
        activeDevices: activeCount,
      }));
      
      // Load geofences (if user has any)
      const { data: geofenceData } = await supabase
        .from('geofences')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (geofenceData) {
        setGeofences(geofenceData);
      }
      
      // Load alerts for user's devices from alerts_v2
      const { data: alertsData, error: alertsError } = await supabase
        .rpc('get_customer_alerts', {
          p_status: null,  // Get all statuses
          p_type: null,    // Get all types
          p_device_id: null,
          p_limit: 50
        });

      if (alertsData && !alertsError) {
        // Map the RPC response to match Alert interface
        const mappedAlerts = alertsData.map((alert: any) => ({
          id: alert.id,
          device_id: alert.device_id,
          type: alert.type || 'general',
          message: alert.message,
          severity: alert.severity || 'medium',
          timestamp: alert.triggered_at,  // alerts_v2 uses triggered_at instead of created_at
          acknowledged: alert.status === 'acknowledged' || alert.status === 'resolved',
          title: alert.title
        }));
        setAlerts(mappedAlerts);
      } else if (alertsError) {
        console.error('Error loading alerts:', alertsError);
      }
      
      // Load GPS data for all devices or selected device
      if (transformedDevices.length > 0) {
        await loadGpsData(selectedDevice || undefined);
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [user, selectedDevice, loadGpsData, isSuperAdmin]);

  useEffect(() => {
    if (isAuthenticated && user) {
      loadData();
    }
  }, [isAuthenticated, user, loadData]);

  useEffect(() => {
    if (devices.length > 0) {
      // Load GPS data for all devices or selected device
      loadGpsData(selectedDevice || undefined);
    }
  }, [selectedDevice, timeFilter, devices, loadGpsData]);

  const handleLogout = async () => {
    console.log('Logout clicked'); // Debug
    setUserMenuOpen(false); // Close menu first
    
    try {
      console.log('Calling signOut...'); // Debug
      await signOut();
      console.log('SignOut successful'); // Debug
      
      // Force a small delay to ensure state is updated, then redirect
      setTimeout(() => {
        if (isAuthenticated) {
          console.log('Still authenticated after signOut, forcing redirect');
          window.location.href = '/';
        }
      }, 100);
      
    } catch (error) {
      console.error('Logout error:', error);
      // Force redirect if logout fails
      window.location.href = '/';
    }
  };

  const exportData = () => {
    const rows = gpsData || [];
    if (rows.length === 0) return;

    const deviceMap = new Map<string, Device>();
    devices.forEach(d => deviceMap.set(d.device_id, d));

    const escapeCsv = (value: any): string => {
      if (value === null || value === undefined) return '';
      const s = String(value);
      if (s.includes('"') || s.includes(',') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const headers = [
      'Device', 'Nickname', 'IMEI', 'ICCID', 'Address', 'Latitude', 'Longitude', 'Time', 'Source', 'Battery', 'Speed', 'Temperature'
    ];

    const lines: string[] = [];
    lines.push(headers.join(','));

    rows.forEach(item => {
      const device = deviceMap.get(item.device_id);

      // Parse raw_data if string
      let parsedRawData: any = null;
      if (typeof item.raw_data === 'string') {
        try { parsedRawData = JSON.parse(item.raw_data); } catch { parsedRawData = null; }
      } else { parsedRawData = item.raw_data as any; }

      const nickname = device?.device_nickname || (item as any).device_nickname || `Device ${item.device_id}`;
      const imei = parsedRawData?.imei || item.device_id;
      const iccid = parsedRawData?.iccid || (item as any)?.iccid || '';
      const address = (item as any).address || '';
      const latitude = item.latitude;
      const longitude = item.longitude;
      const timeIso = new Date(item.created_at).toISOString();
      const source = (item as any).location_source || 'GPS';
      const battery = item.battery_percentage ?? '';
      const speed = item.speed ?? '';
      const temperature = item.temperature ?? '';

      const dataRow = [
        item.device_id, nickname, imei, iccid, address, latitude, longitude, timeIso, source, battery, speed, temperature
      ].map(escapeCsv).join(',');

      lines.push(dataRow);
    });

    const csvContent = '\uFEFF' + lines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const datePart = new Date().toISOString().replace(/[:T]/g, '-').split('.')[0];
    link.download = `gps_data_${selectedDevice || 'all'}_${datePart}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleEditDevice = () => {
    if (selectedDevice) {
      const device = devices.find(d => d.id === selectedDevice);
      if (device) {
        setSelectedDeviceForEdit(device);
        setEditModalOpen(true);
      }
    }
  };

  const handleSaveDevice = async (deviceId: string, newName: string) => {
    try {
      // Use secure RPC that enforces multi-tenant permissions server-side
      const { error: updateError } = await supabase.rpc('update_device_nickname', {
        p_device_id: deviceId,
        p_new_name: newName
      });
        
      if (updateError) {
        console.error('Update error:', updateError);
        throw updateError;
      }
      
      // Optimistically update local state so UI reflects change immediately
      setDevices(prev => prev.map(d => {
        if (d.device_id === deviceId) {
          return { ...d, device_nickname: newName };
        }
        return d;
      }));

      // Force refresh the devices data
      await loadData();
      
      // Also force refresh GPS data to ensure everything is in sync
      await loadGpsData();
    } catch (error) {
      console.error('Error updating device:', error);
      throw error; // Re-throw so the modal can show the error
    }
  };

  const handleGeofenceCreated = (center: [number, number], radius: number) => {
    setTemporaryGeofence({ center, radius });
    setIsDrawingGeofence(false);
    setGeofenceModalOpen(true);
  };

  if (!isAuthenticated) {
    return <LoginWithSignup />;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Clean Minimalist Header - Single Row */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-[2000]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Brand */}
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-traqpack-yellow rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-gray-900" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">TraqPack</h1>
                <p className="text-xs text-gray-500">Real-time GPS Tracking Platform</p>
              </div>
            </div>

            {/* Navigation Tabs - Center */}
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setActiveTab('tracking')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'tracking'
                    ? 'bg-traqpack-yellow text-gray-900'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Activity className="w-4 h-4 inline mr-2" />
                Live Tracking
              </button>
              <button
                onClick={() => setActiveTab('devices')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'devices'
                    ? 'bg-traqpack-yellow text-gray-900'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Smartphone className="w-4 h-4 inline mr-2" />
                Devices
              </button>
              <button
                onClick={() => setActiveTab('shipments')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'shipments'
                    ? 'bg-traqpack-yellow text-gray-900'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Package className="w-4 h-4 inline mr-2" />
                Shipments
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === 'users'
                    ? 'bg-traqpack-yellow text-gray-900'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <UsersIcon className="w-4 h-4 inline mr-2" />
                Users
              </button>
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-3">
              <button 
                onClick={exportData}
                className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>
              
              <button 
                onClick={() => setChatPanelOpen(!chatPanelOpen)}
                className="flex items-center px-3 py-2 text-sm bg-traqpack-yellow text-gray-900 hover:bg-yellow-400 rounded-lg transition-colors"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                AI Assistant
              </button>

              {/* User Dropdown */}
              <div className="relative">
                <button 
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <User className="w-4 h-4" />
                  <span>{userProfile?.full_name || user?.email?.split('@')[0] || 'My Account'}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>

                {userMenuOpen && (
                  <div data-user-menu className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-[3000]">
                    {/* User Info */}
                    <div className="px-4 py-3 border-b border-gray-100">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-gray-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{userProfile?.full_name || user?.email?.split('@')[0] || 'User'}</div>
                          <div className="text-sm text-gray-500">{userProfile?.email || user?.email}</div>
                        </div>
                      </div>
                    </div>

                    {/* Menu Items */}
                    <div className="py-2">
                      <button 
                        onClick={() => window.location.href = '/account'}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                      >
                        <User className="w-4 h-4 text-gray-500" />
                        <span>My Account</span>
                      </button>
                      <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-3">
                        <Settings className="w-4 h-4 text-gray-500" />
                        <span>Settings</span>
                      </button>
                      <button 
                        onClick={() => {
                          console.log('Help Center clicked - opening documentation');
                          window.open('https://docs.traqpack.com/getting-started-guide', '_blank', 'noopener,noreferrer');
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                      >
                        <HelpCircle className="w-4 h-4 text-gray-500" />
                        <span>Help Center</span>
                      </button>
                      <a 
                        href="https://mail.google.com/mail/?view=cm&fs=1&to=support@traqpack.com&su=Support%20Request%20-%20TraqPack"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-3"
                      >
                        <MessageCircle className="w-4 h-4 text-gray-500" />
                        <span>Support</span>
                      </a>
                    </div>

                    {/* Purchase New Device */}
                    <div className="border-t border-gray-100 pt-2">
                      <a 
                        href="/purchase"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full text-left px-4 py-2 text-sm text-traqpack-yellow font-semibold hover:bg-yellow-50 flex items-center space-x-3 block"
                      >
                        <CreditCard className="w-4 h-4" />
                        <span>Buy TraqPack Device</span>
                      </a>
                    </div>

                    {/* Logout */}
                    <div className="border-t border-gray-100 pt-2">
                      <div 
                        onClick={(e) => {
                          console.log('Sign out button clicked!'); // Debug
                          e.preventDefault();
                          e.stopPropagation();
                          handleLogout();
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-3 cursor-pointer"
                        role="button"
                        tabIndex={0}
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Sign out</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Zone 1: Top Area - Big Numbers (Not sticky - scrolls with page) */}
      <div className="bg-white border-b border-gray-100 px-6 py-4">
        <StatsGrid stats={stats} loading={loading} />
      </div>

      <main className="w-full">
        {activeTab === 'tracking' ? (
          <>
            {/* Zone 2 & 3: Main Content with Sidebar */}
            <div className="flex gap-4 flex-col lg:flex-row w-full px-2 py-6">
              {/* Zone 2: Left Sidebar */}
              <div className="w-full lg:w-72 xl:w-80 flex-shrink-0">
                <div className="sidebar-card p-4">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">Filters & Controls</h2>
                    
                    {/* AI Query Bar integrated here */}
                    <div className="mb-6">
                      <label className="sidebar-label">AI Assistant</label>
                      <AIQueryBar onQueryResult={setGpsData} compact={true} />
                    </div>
                    
                    {/* Live Status */}
                    <div className="flex items-center space-x-2 mb-6 bg-green-50 rounded-xl px-4 py-3 border border-green-200">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-sm font-medium text-green-700">Real-time tracking active</span>
                    </div>
                    
                    {/* Time Filter */}
                    <div className="sidebar-section mb-8">
                      <label className="sidebar-label">Time Period</label>
                  <TimeFilter 
                    activeFilter={timeFilter} 
                    onFilterChange={setTimeFilter}
                    vertical={true}
                  />
                    </div>
                    
                    {/* Device Selector */}
                    <div className="sidebar-section mb-8">
                      <label className="sidebar-label">Select Device</label>
                  <DeviceSelector
                    devices={devices}
                    selectedDevice={selectedDevice}
                    onSelectDevice={setSelectedDevice}
                    onEditDevice={handleEditDevice}
                  />
                    </div>
                    
                    {/* Device Stats */}
                    <DeviceStats 
                      device={devices.find(d => d.id === selectedDevice) || null}
                    />
                    
                    {/* Geofence Manager */}
                    <div className="sidebar-section">
                      <label className="sidebar-label">My Locations</label>
                  <GeofenceManager
                    geofences={geofences}
                    onAddGeofence={() => setIsDrawingGeofence(true)}
                    onToggleGeofence={async (id, active) => {
                      await supabase
                        .from('geofences')
                        .update({ active })
                        .eq('id', id)
                        .eq('user_id', user?.id);
                      loadData();
                    }}
                    onDeleteGeofence={async (id) => {
                      await supabase
                        .from('geofences')
                        .delete()
                        .eq('id', id)
                        .eq('user_id', user?.id);
                      loadData();
                    }}
                    onEditGeofence={(id) => {
                      const geofence = geofences.find(g => g.id === id);
                      if (geofence) {
                        setEditingGeofence(geofence);
                        setGeofenceModalOpen(true);
                      }
                    }}
                    selectedGeofence={selectedGeofence}
                    onSelectGeofence={setSelectedGeofence}
                  />
                    </div>
                    
                    {/* Geofence Notifications */}
                    <div className="sidebar-section mt-6">
                      <GeofenceNotifications 
                        userId={user?.id}
                        limit={5}
                  />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Zone 3: Main Content Area */}
              <div className="flex-1 space-y-6">
                
                {/* Map Card */}
                <div className="main-card p-6">
                  {/* Header */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold text-gray-900">Live Tracking Map</h2>
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <MapPin className="w-4 h-4" />
                        <span>{gpsData.length} locations</span>
                      </div>
                    </div>
                  </div>
                
                {/* Map Container */}
                <div className="h-[400px] md:h-[500px] lg:h-[600px] rounded-xl overflow-hidden shadow-inner bg-gray-100 relative">
                  {/* Debug border to see the container - Hidden in production */}
                  {process.env.NODE_ENV === 'development' && (
                    <div className="absolute top-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded z-10">
                    Map Container ({gpsData.length} points)
                  </div>
                  )}
                      {loading ? (
                        <div className="flex items-center justify-center h-full">
                          <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-traqpack-yellow mx-auto mb-4"></div>
                            <p className="text-gray-600">Loading map...</p>
                          </div>
                        </div>
                      ) : (
                        <TraqPackMap 
                          gpsData={gpsData} 
                          timeFilter={timeFilter}
                          loading={loading}
                          selectedDevice={selectedDevice}
                          geofences={geofences}
                          selectedGeofence={selectedGeofence}
                          isDrawingGeofence={isDrawingGeofence}
                          onGeofenceCreated={handleGeofenceCreated}
                        />
                      )}
                    </div>
                  
                  {/* Drawing instructions */}
                  {isDrawingGeofence && (
                    <div className="mt-4 p-4 bg-traqpack-yellow/20 rounded-xl border-2 border-traqpack-yellow">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <MapPinned className="w-5 h-5 text-black" />
                          <span className="font-medium">Drawing Geofence</span>
                        </div>
                        <button
                          onClick={() => setIsDrawingGeofence(false)}
                          className="text-sm text-black/70 hover:text-black font-medium"
                        >
                          Cancel
                        </button>
                      </div>
                      <p className="text-sm text-black/70 mt-2">
                        <strong>Click and hold</strong> on the map to set the center, then <strong>drag outward</strong> to set the radius. 
                        <strong>Release</strong> when you have the desired size (minimum 50m).
                      </p>
                    </div>
                  )}
                  
                  {/* Map Legend */}
                  <div className="mt-4 flex items-center justify-center space-x-6 text-sm">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-traqpack-yellow rounded-full border-2 border-black"></div>
                      <span className="text-black/70">Device Location</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      <span className="text-black/70">Movement Path</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-blue-500/30 rounded-full border-2 border-blue-500"></div>
                      <span className="text-black/70">Geofence</span>
                    </div>
                  </div>
                </div>
              
                {/* Alerts Panel */}
                <AlertsPanel 
                  alerts={alerts} 
                  loading={loading}
                  onDelete={async (alertId) => {
                    try {
                      const { error } = await supabase
                        .rpc('delete_alert', { p_alert_id: alertId });
                      
                      if (error) throw error;
                      
                      // Remove alert from local state
                      setAlerts(alerts.filter(alert => alert.id !== alertId));
                    } catch (error) {
                      console.error('Error deleting alert:', error);
                    }
                  }}
                  onClearAll={async () => {
                    try {
                      const { error } = await supabase
                        .rpc('delete_all_alerts');
                      
                      if (error) throw error;
                      
                      // Clear all alerts from local state
                      setAlerts([]);
                    } catch (error) {
                      console.error('Error clearing all alerts:', error);
                    }
                  }}
                />

                {/* Data Table */}
                <div className="main-card p-6">
                  <DataTable 
                    data={gpsData} 
                    devices={devices}
                    loading={loading}
                    searchTerm={searchTerm}
                    onRefresh={() => loadGpsData(selectedDevice || undefined)}
                    refreshing={gpsLoading}
                  />
                </div>
              </div>
            </div>
          </>
        ) : activeTab === 'devices' ? (
          <div className="w-full px-2 py-6">
            <DevicesPageUserFiltered 
              onEditDevice={(device) => {
                setSelectedDeviceForEdit(device);
                setEditModalOpen(true);
              }}
            />
          </div>
        ) : activeTab === 'shipments' ? (
          <div className="w-full px-2 py-6">
            <ShipmentsPage />
          </div>
        ) : activeTab === 'users' ? (
          <div className="w-full px-2 py-6">
            <UsersPage />
          </div>
        ) : null}
      </main>

      {/* Modals */}
      {editModalOpen && selectedDeviceForEdit && (
        <EditDeviceModal
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedDeviceForEdit(null);
          }}
          device={selectedDeviceForEdit}
          onSave={handleSaveDevice}
        />
      )}

      <GeofenceModal
        isOpen={geofenceModalOpen}
        onClose={() => {
          setGeofenceModalOpen(false);
          setTemporaryGeofence(null);
          setEditingGeofence(null);
        }}
        onSave={async (geofence) => {
          if (editingGeofence) {
            const { error } = await supabase
              .from('geofences')
              .update(geofence)
              .eq('id', editingGeofence.id)
              .eq('user_id', user?.id);
            
            if (error) throw error;
            
            setGeofences(geofences.map(g => 
              g.id === editingGeofence.id ? { ...g, ...geofence } : g
            ));
          } else {
            const { data, error } = await supabase
              .from('geofences')
              .insert([{ ...geofence, user_id: user?.id }])
              .select()
              .single();
            
            if (error) throw error;
            if (data) {
              setGeofences([...geofences, data]);
            }
          }
          
          // Clear temporary states after successful save
          setTemporaryGeofence(null);
          setEditingGeofence(null);
        }}
        temporaryGeofence={temporaryGeofence}
        geofence={editingGeofence}
      />

      {/* AI Chat Panel */}
      <AIChatPanel
        isOpen={chatPanelOpen}
        onClose={() => setChatPanelOpen(false)}
      />
    </div>
  );
}

export default AppMultiTenant;